import type { ReactNode } from 'react';
import type { JobMarketDto } from 'src/contracts/job-market';

import { useRef, useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import {
  clearStorageJobs,
  hasJobsInStorage,
  saveJobsToStorage,
  getJobsFromStorage,
} from 'src/utils/local-storage';

import { apiClient } from 'src/lib/api-client';
import { useExtensionCommunication } from 'src/lib/extension/use-extension-communication';

// Add a global readiness flag to the window as early as possible
// This script runs immediately when imported
(() => {
  try {
    // Set a global flag that the extension can check
    window.extensionPageReady = true;

    // Add a global function for the extension to call directly
    window.sendExtensionMessage = (type, data) => {
      window.postMessage(
        {
          type,
          source: 'JOBJOURNEY_APP',
          data,
          timestamp: Date.now(),
        },
        '*'
      );
    };

    // Add early handler only in browser environment
    if (typeof window !== 'undefined') {
      // Also set up an early message listener for critical messages
      const earlyHandler = (event: MessageEvent) => {
        // Skip non-extension messages to reduce processing
        if (!event.data?.type) return;

        // Handle version check messages (critical for extension compatibility)
        if (event.data?.type === 'VERSION_CHECK') {
          console.log('🟢 Received version check request:', event.data);

          // Extract data from the incoming message
          const extensionVersion = event.data.data?.version || '0.0.1';
          const messageId =
            event.data.messageId || event.data.data?.messageId || `auto-${Date.now()}`;
          const requestId = event.data.data?.requestId;

          apiClient
            .post<{
              isCompatible: boolean;
              minimumVersion?: string;
              latestVersion?: string;
              message?: string;
            }>('extension/version-check', {
              version: extensionVersion,
              extensionId: 'extension',
            })
            .then((response) => {
              console.log('🟢 API version check response:', response);

              // Send response back to the extension
              const verResponse = {
                type: 'VERSION_CHECK_RESPONSE',
                source: 'JOBJOURNEY_APP',
                messageId,
                data: {
                  isCompatible: response.isCompatible,
                  minimumVersion: response.minimumVersion || '0.0.1',
                  latestVersion: response.latestVersion || extensionVersion,
                  message:
                    response.message ||
                    `Your extension v${extensionVersion} is compatible with this website`,
                  originalMessageId: messageId,
                  requestId,
                },
                timestamp: Date.now(),
              };

              console.log('🟢 Sending version check response:', verResponse);
              window.postMessage(verResponse, '*');
            })
            .catch((error) => {
              console.error('🔴 Error checking version with API:', error);

              // Even on error, return a compatible response to avoid blocking functionality
              const fallbackResponse = {
                type: 'VERSION_CHECK_RESPONSE',
                source: 'JOBJOURNEY_APP',
                messageId,
                data: {
                  isCompatible: true, // Default to compatible on error
                  minimumVersion: '0.0.1',
                  latestVersion: '0.0.1',
                  message: `Error checking version, but continuing with extension v${extensionVersion}`,
                  originalMessageId: messageId,
                  error: String(error),
                  requestId,
                },
                timestamp: Date.now(),
              };

              window.postMessage(fallbackResponse, '*');
            });
        }
      };

      // Add the early handler
      window.addEventListener('message', earlyHandler);
      console.log('🟢 JobJourney website ready for extension communication');
    }
  } catch (error) {
    console.error('Error setting up extension communication:', error);
    // Silently continue - we don't want to break the app if this fails
  }
})();

interface ExtensionContextType {
  jobs: JobMarketDto[];
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  handleDownloadExtension: () => void;
  setJobs: (jobs: JobMarketDto[]) => void;
  hasSessionJobs: boolean;
  clearJobs: () => void;
  isExtensionInstalled: boolean | null;
}

// Create context with default values
const ExtensionContext = createContext<ExtensionContextType | null>(null);

interface ExtensionProviderProps {
  children: ReactNode;
}

export function ExtensionProvider({ children }: ExtensionProviderProps) {
  // Use the extension communication hook
  const { jobs: hookJobs, setJobs: setHookJobs } = useExtensionCommunication();

  // State to track if we have jobs in local storage
  const [hasSessionJobs, setHasSessionJobs] = useState<boolean>(hasJobsInStorage());
  // Jobs state that combines hook jobs and session storage
  const [jobs, setJobsState] = useState<JobMarketDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState<boolean | null>(null);

  const initializedRef = useRef(false);

  // Initialize jobs from local storage on mount only
  useEffect(() => {
    const storageJobs = getJobsFromStorage();
    if (storageJobs.length > 0) {
      setJobsState(storageJobs);
      setHasSessionJobs(true);
      console.log(`Loaded ${storageJobs.length} jobs from local storage`);
    }
    initializedRef.current = true;
  }, []);

  // Detect whether the Chrome extension is installed via ping/pong
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const handlePong = (event: MessageEvent) => {
      if (event.data?.type === 'JOBJOURNEY_EXTENSION_PONG') {
        clearTimeout(timeout);
        setIsExtensionInstalled(true);
      }
    };

    window.addEventListener('message', handlePong);
    window.postMessage({ type: 'JOBJOURNEY_EXTENSION_PING', source: 'JOBJOURNEY_APP' }, '*');

    timeout = setTimeout(() => {
      setIsExtensionInstalled((prev) => (prev === null ? false : prev));
    }, 2000);

    return () => {
      window.removeEventListener('message', handlePong);
      clearTimeout(timeout);
    };
  }, []);

  // Sync with hook jobs when they change (after initialization)
  useEffect(() => {
    if (!initializedRef.current) return;
    if (hookJobs.length > 0) {
      setJobsState(hookJobs);
      saveJobsToStorage(hookJobs);
      setHasSessionJobs(true);
    }
  }, [hookJobs]);

  // Set jobs function that updates both state and local storage
  const setJobs = useCallback(
    (newJobs: JobMarketDto[]) => {
      setJobsState(newJobs);
      saveJobsToStorage(newJobs);
      setHookJobs(newJobs);
      setHasSessionJobs(newJobs.length > 0);
    },
    [setHookJobs]
  );

  // Clear all jobs
  const clearJobs = useCallback(() => {
    setJobsState([]);
    clearStorageJobs();
    setHookJobs([]);
    setHasSessionJobs(false);
  }, [setHookJobs]);

  // Handle downloading the extension
  const handleDownloadExtension = useCallback(() => {
    // This calls the downloadExtension method in the extension service via the hook
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      jobs,
      isLoading,
      setIsLoading,
      handleDownloadExtension,
      setJobs,
      hasSessionJobs,
      clearJobs,
      isExtensionInstalled,
    }),
    [jobs, isLoading, handleDownloadExtension, setJobs, hasSessionJobs, clearJobs, isExtensionInstalled]
  );

  return <ExtensionContext.Provider value={contextValue}>{children}</ExtensionContext.Provider>;
}

export function useExtension(): ExtensionContextType {
  const context = useContext(ExtensionContext);
  if (!context) {
    throw new Error('useExtension must be used within an ExtensionProvider');
  }
  return context;
}
